/**
 * Reusable Slider Module
 * Can be used for multiple sliders throughout the site
 * 
 * Usage:
 * initSlider(selector, options)
 * 
 * Example:
 * initSlider('#my-slider', {
 *     type: 'loop',
 *     autoplay: true,
 *     interval: 3000
 * });
 */

/**
 * Initialize a Splide slider
 * @param {string} selector - CSS selector or element ID
 * @param {object} options - Splide configuration options
 * @returns {Splide|null} Splide instance or null if element not found
 */
function initSlider(selector, options = {}) {
    const element = typeof selector === 'string'
        ? document.querySelector(selector)
        : selector;

    if (!element) {
        return null;
    }

    // Default options
    const defaultOptions = {
        type: 'loop',
        autoplay: true,
        interval: 3400,
        speed: 800,
        pauseOnHover: false,
        arrows: true,
        pagination: true,
        drag: true,
        keyboard: true,
    };

    // Merge with custom options
    const config = { ...defaultOptions, ...options };

    return new Splide(element, config).mount();
}

/**
 * Initialize announcement bar slider with specific settings
 */
function initAnnouncementSlider() {
    return initSlider('#announcement-slider', {
        type: 'loop',
        autoplay: true,
        interval: 3400,
        speed: 800,
        pauseOnHover: false,
        arrows: false,
        pagination: false,
        drag: false,
        keyboard: false,
        perPage: 1,
        perMove: 1,
        gap: 0,
    });
}

/**
 * Initialize hero section slider with specific settings
 */
function initHeroSlider() {
    return initSlider('#hero-slider', {
        type: 'loop',
        autoplay: true,
        interval: 5000,
        speed: 1000,
        pauseOnHover: true,
        arrows: true,
        pagination: true,
        drag: true,
        keyboard: true,
        rewind: true,
        gap: 0,
    });
}

/**
 * Initialize testimonials slider with specific settings
 */
function initTestimonialsSlider() {
    return initSlider('#testimonials-slider', {
        type: 'slide',
        autoplay: false,
        perPage: 3,
        perMove: 1,
        gap: 32,
        arrows: true,
        pagination: true,
        drag: true,
        keyboard: true,
        trimSpace: true,
        breakpoints: {
            968: {
                perPage: 2,
                gap: 24,
            },
            768: {
                perPage: 1,
                gap: 24,
            },
        },
    });
}

/**
 * Initialize methods slider
 */
function initMethodsSlider() {
    return initSlider('#methods-slider', {
        type: 'slide',
        autoplay: false,
        rewind: false,
        perPage: 3,
        perMove: 1,
        gap: 24,
        arrows: true,
        pagination: false,
        drag: true,
        touch: true,
        keyboard: true,
        trimSpace: false,
        padding: { right: '0px' },
        breakpoints: {
            1200: {
                perPage: 2,
                gap: 20,
                padding: { right: '10%' },
            },
            768: {
                perPage: 1,
                gap: 16,
                padding: { right: '15%' },
            },
        },
    });
}

/**
 * Initialize courses slider (Akademie)
 */
function initCoursesSlider() {
    const sliders = document.querySelectorAll('.courses__slider:not(.is-initialized)');
    sliders.forEach(slider => {
        slider.classList.add('is-initialized');
        
        const slideCount = slider.querySelectorAll('.splide__slide').length;
        if (slideCount <= 4) {
            slider.classList.add('courses__slider--fewer-slides');
        }

        initSlider(slider, {
            type: 'slide',
            autoplay: false,
            rewind: false,
            perPage: 4,
            perMove: 1,
            gap: 24,
            arrows: slideCount > 4,
            pagination: false,
            drag: true,
            touch: true,
            keyboard: true,
            trimSpace: true,
            breakpoints: {
                1200: {
                    perPage: 3,
                    gap: 20,
                    arrows: slideCount > 3,
                },
                992: {
                    perPage: 2,
                    gap: 18,
                    arrows: slideCount > 2,
                },
                640: {
                    perPage: 1,
                    gap: 16,
                    arrows: slideCount > 1,
                },
            },
        });
    });
}

/**
 * Initialize online courses slider (Akademie)
 */
function initOnlineCoursesSlider() {
    // Both types loads via initCoursesSlider using class query
    return initCoursesSlider();
}

/**
 * Initialize student results slider
 */
function initStudentResultsSlider() {
    return initSlider('#student-results-slider', {
        type: 'slide',
        autoplay: false,
        rewind: false,
        perPage: 3,
        perMove: 1,
        gap: 24,
        arrows: true,
        pagination: false,
        drag: false,
        touch: false,
        keyboard: true,
        trimSpace: true,
        breakpoints: {
            1200: {
                perPage: 2,
                gap: 20,
            },
            768: {
                perPage: 1,
                gap: 16,
            },
        },
    });
}

/**
 * Initialize video slider
 */
function initVideoSlider() {
    return initSlider('#videos-slider', {
        type: 'loop',
        autoplay: false,
        perPage: 3,
        perMove: 1,
        gap: 24,
        arrows: true,
        pagination: false,
        drag: true,
        keyboard: true,
        trimSpace: true,
        breakpoints: {
            1200: {
                perPage: 2,
                gap: 20,
            },
            768: {
                perPage: 1,
                gap: 16,
            },
        },
    });
}

